import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  TablePagination,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
} from '@mui/material';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import {
  Download,
  MonetizationOn,
  TrendingUp,
  Group,
  Sync,
  Insights,
  Search,
  CalendarMonth,
  FilterAlt,
  ConfirmationNumber,
} from '@mui/icons-material';
import ReportTable from './ReportTable';
import '../../styles/report.css';

const AdminReports = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [bookingType, setBookingType] = useState('upcoming');

  const handleTabChange = (event, newTab) => setActiveTab(newTab);
  const handleSearchChange = (event) => setSearchText(event.target.value);
  const handleMonthChange = (event) => setSelectedMonth(event.target.value);
  const handleYearChange = (event) => setSelectedYear(event.target.value);
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleBookingTypeChange = (event) => setBookingType(event.target.value);

  const handleExportCSV = () => {
    const currentData =
      activeTab === 0
        ? customerData
        : activeTab === 1
        ? vendorData
        : activeTab === 2
        ? leadData
        : bookingData;

    const worksheet = XLSX.utils.json_to_sheet(currentData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Data');
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(
      blob,
      `report_${
        activeTab === 0
          ? 'customers'
          : activeTab === 1
          ? 'vendors'
          : activeTab === 2
          ? 'leads'
          : 'bookings'
      }_${getMonthName(selectedMonth)}_${selectedYear}.xlsx`
    );
  };

  const getMonthName = (monthNumber) => {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return monthNames[monthNumber - 1];
  };

  const customerData = [
    {
      name: 'John Doe',
      email: 'john@example.com',
      phone: '123-456-7890',
      address: '123 Main St',
      lastActive: '2023-12-01',
      dateAdded: '2022-01-15',
      booking_number: 12349,
      booking_status: 'Active',
    },
    {
      name: 'Jane Smith',
      email: 'jane@example.com',
      phone: '234-567-8901',
      address: '456 Oak Ave',
      lastActive: '2023-11-30',
      dateAdded: '2021-06-10',
      booking_number: 12329,
      booking_status: 'Inactive',
    },
  ];

  const vendorData = [
    {
      agencyName: 'RK',
      contactPerson: 'Alice',
      email: 'vendor1@example.com',
      phone: '9876543210',
      address: '789 Pine Rd',
      registrationNumber: 'A123',
      gstin: 'GSTIN123',
      pan: 'PAN123',
      lastActive: '2023-12-02',
      createdDate: '2020-03-15',
    },
    {
      agencyName: 'PK',
      contactPerson: 'Bob',
      email: 'vendor2@example.com',
      phone: '8765432109',
      address: '123 Maple Blvd',
      registrationNumber: 'B456',
      gstin: 'GSTIN456',
      pan: 'PAN456',
      lastActive: '2023-12-01',
      createdDate: '2019-07-22',
    },
  ];

  const leadData = [
    {
      leadId: 'L001',
      source: 'Website',
      enquiryDate: '2023-12-01',
      enquiryTime: '10:00 AM',
      status: 'Open',
      cost: '$1000',
      profit: '$500',
      followUpDate: '2023-12-05',
      modeOfPayment: 'Credit',
    },
    {
      leadId: 'L002',
      source: 'Referral',
      enquiryDate: '2023-11-28',
      enquiryTime: '2:30 PM',
      status: 'Closed',
      cost: '$2000',
      profit: '$1000',
      followUpDate: '2023-12-07',
      modeOfPayment: 'Debit',
    },
  ];

  const bookingData = [
    {
      booking_number: 'BK-1234',
      booking_serial_no: 'BK001',
      date_of_booking: '2025-03-01 10:30 AM',
      booked_user_name: 'John Doe',
      booked_for: 'James Smith',
      service: 'Airport Lounge Access',
      service_type: 'Departure',
      number_of_adults: 2,
      number_of_children: 1,
      number_of_infants: 0,
      pnr_number: 'ABC123',
      datetime_of_arrival: '2025-03-10 14:00',
      arrival_airline: 'Emirates',
      arrival_flight_number: 'EK506',
      datetime_of_departure: '2025-03-15 22:30',
      departure_airline: 'Qatar Airways',
      departure_flight_number: 'QR753',
      payment_status: 'Paid',
    },
    {
      booking_number: 'BK-5678',
      booking_serial_no: 'BK002',
      date_of_booking: '2025-02-15 15:45 PM',
      booked_user_name: 'Jane Smith',
      booked_for: 'Robert Johnson',
      service: 'Meet & Greet',
      service_type: 'Arrival',
      number_of_adults: 1,
      number_of_children: 0,
      number_of_infants: 1,
      pnr_number: 'XYZ789',
      datetime_of_arrival: '2025-03-05 08:15',
      arrival_airline: 'Lufthansa',
      arrival_flight_number: 'LH720',
      datetime_of_departure: '2025-03-12 16:45',
      departure_airline: 'British Airways',
      departure_flight_number: 'BA104',
      payment_status: 'Pending',
    },
  ];

  // Get summary data based on active tab
  const getSummaryData = () => {
    if (activeTab === 0) {
      return [
        {
          label: 'Total Customers',
          value: '2,347',
          icon: <Group sx={{ color: '#4361ee' }} />,
        },
        {
          label: 'Active Customers',
          value: '1,892',
          icon: <Insights sx={{ color: '#f72585' }} />,
        },
        {
          label: 'New This Month',
          value: '126',
          icon: <TrendingUp sx={{ color: '#4cc9f0' }} />,
        },
        {
          label: 'Retention Rate',
          value: '85%',
          icon: <Sync sx={{ color: '#7209b7' }} />,
        },
        {
          label: 'Avg. Bookings',
          value: '3.2',
          icon: <MonetizationOn sx={{ color: '#4ade80' }} />,
        },
      ];
    } else if (activeTab === 1) {
      return [
        {
          label: 'Total Vendors',
          value: '487',
          icon: <Group sx={{ color: '#4361ee' }} />,
        },
        {
          label: 'Active Vendors',
          value: '342',
          icon: <Insights sx={{ color: '#f72585' }} />,
        },
        {
          label: 'New This Month',
          value: '24',
          icon: <TrendingUp sx={{ color: '#4cc9f0' }} />,
        },
        {
          label: 'Fulfillment Rate',
          value: '92%',
          icon: <Sync sx={{ color: '#7209b7' }} />,
        },
        {
          label: 'Avg. Response',
          value: '2.4 hrs',
          icon: <MonetizationOn sx={{ color: '#4ade80' }} />,
        },
      ];
    } else if (activeTab === 2) {
      return [
        {
          label: 'Total Leads',
          value: '824',
          icon: <Group sx={{ color: '#4361ee' }} />,
        },
        {
          label: 'Conversion Rate',
          value: '32%',
          icon: <Insights sx={{ color: '#f72585' }} />,
        },
        {
          label: 'New This Month',
          value: '156',
          icon: <TrendingUp sx={{ color: '#4cc9f0' }} />,
        },
        {
          label: 'Avg. Response',
          value: '4.5 hrs',
          icon: <Sync sx={{ color: '#7209b7' }} />,
        },
        {
          label: 'Revenue',
          value: '₹12.4L',
          icon: <MonetizationOn sx={{ color: '#4ade80' }} />,
        },
      ];
    } else {
      return [
        {
          label: 'Total Bookings',
          value: '1,248',
          icon: <ConfirmationNumber sx={{ color: '#4361ee' }} />,
        },
        {
          label: 'Upcoming',
          value: '342',
          icon: <Insights sx={{ color: '#f72585' }} />,
        },
        {
          label: 'Completed',
          value: '856',
          icon: <TrendingUp sx={{ color: '#4cc9f0' }} />,
        },
        {
          label: 'Cancelled',
          value: '50',
          icon: <Sync sx={{ color: '#7209b7' }} />,
        },
        {
          label: 'Avg. Value',
          value: '₹4,850',
          icon: <MonetizationOn sx={{ color: '#4ade80' }} />,
        },
      ];
    }
  };

  return (
    <Box className='report-dashboard'>
      {/* Tab Navigation */}
      <Box className='tabs'>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label='Report Tabs'
        >
          <Tab label='Customers' />
          <Tab label='Vendors' />
          <Tab label='Leads' />
          <Tab label='Bookings' />
        </Tabs>
      </Box>

      {/* Filters */}
      <Box className='controls'>
        <Box className='search-filter'>
          <TextField
            label='Search'
            value={searchText}
            onChange={handleSearchChange}
            variant='outlined'
            size='small'
            sx={{ width: { xs: '100%', sm: '250px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size='small' sx={{ minWidth: 120 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              label='Month'
              startAdornment={
                <InputAdornment position='start'>
                  <CalendarMonth fontSize='small' />
                </InputAdornment>
              }
            >
              {Array.from({ length: 12 }).map((_, index) => (
                <MenuItem key={index + 1} value={index + 1}>
                  {getMonthName(index + 1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              label='Year'
            >
              <MenuItem value={2025}>2025</MenuItem>
              <MenuItem value={2024}>2024</MenuItem>
              <MenuItem value={2023}>2023</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Vertical Summary Section */}
      <Box className='summary-section'>
        {getSummaryData().map((item, index) => (
          <Tooltip key={index} title={`Details about ${item.label}`} arrow>
            <Box className='metric'>
              <Box className='icon'>{item.icon}</Box>
              <Typography variant='h6'>{item.label}</Typography>
              <Typography variant='body1'>{item.value}</Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* Report Content */}
      <Box className='report-content'>
        <Box className='report-head'>
          <Typography variant='h6' className='section-title'>
            {activeTab === 0 &&
              `Customer Report - ${getMonthName(
                selectedMonth
              )} ${selectedYear}`}
            {activeTab === 1 &&
              `Vendor Report - ${getMonthName(selectedMonth)} ${selectedYear}`}
            {activeTab === 2 &&
              `Lead Report - ${getMonthName(selectedMonth)} ${selectedYear}`}
            {activeTab === 3 &&
              `Booking Report (${bookingType}) - ${getMonthName(
                selectedMonth
              )} ${selectedYear}`}
          </Typography>
          <Button
            startIcon={<Download />}
            variant='contained'
            className='report-export-button'
            onClick={handleExportCSV}
          >
            Export Report
          </Button>
        </Box>

        {activeTab === 0 && (
          <ReportTable
            data={customerData}
            type='customers'
            customFilters={{
              page: page + 1,
              rowsPerPage: rowsPerPage,
            }}
          />
        )}
        {activeTab === 1 && (
          <ReportTable
            data={vendorData}
            type='vendors'
            customFilters={{
              page: page + 1,
              rowsPerPage: rowsPerPage,
            }}
          />
        )}
        {activeTab === 2 && <ReportTable data={leadData} type='leads' />}
        {activeTab === 3 && (
          <ReportTable
            data={bookingData}
            type='bookings'
            customFilters={{
              page: page + 1,
              rowsPerPage: rowsPerPage,
            }}
          />
        )}
      </Box>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component='div'
        count={
          activeTab === 0
            ? customerData.length
            : activeTab === 1
            ? vendorData.length
            : activeTab === 2
            ? leadData.length
            : bookingData.length
        }
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default AdminReports;
